// @flow
import { Map } from "immutable"
import type { Dispatch } from "redux"
import { createSelector } from "reselect"

import type { FlightId } from "../../../../../../../types/primitiveTypes"
import type { Step } from "../consts/constants"
import {
  PAX_ATA_ACTIONS,
  CONFIRM_FLIGHT_SELECTION,
  CONFIRM_FLIGHT_SELECTION_FAILURE,
  CONFIRM_FLIGHT_SELECTION_SUCCESS,
  LOAD_GUARANTEE_COVER_FAILURE,
  LOAD_GUARANTEE_COVER_REQUEST,
  LOAD_GUARANTEE_COVER_SUCCESS,
  LOAD_GUARANTEE_COVER,
  REINITIALIZE,
  SET_ACTIVE_FLIGHT,
  SET_STEP,
  STEPS,
  SUBMIT_SELECTED_FLIGHT,
  SUBMIT_SELECTED_FLIGHT_SUCCESS,
} from "../consts/constants"
import {
  confirmAta,
  confirmFlightSelection,
  getGuaranteeCoverage,
} from "../../../../../../../services/bookings/bookingsApi"
import {
  bidSelector,
  bookingDataSelector,
  bookingFlightIdsSelector,
  bookingFlightsSelector,
} from "../../../services/selector"
import type { actionWithPayload, typeReducer } from "../consts/types"
import Price from "common/records/Price"

// eslint-disable-next-line flowtype/no-weak-types
type GetState = () => Object

// REDUCER’s INITIAL STATE
const INITIAL_STATE: typeReducer = Map({
  loading: false,
  step: STEPS.SELECTION,
  price: new Price({ amount: 0 }),
  flightId: null,
})

// REDUCER
export default function passengerAtaReducer(
  state: typeReducer = INITIAL_STATE,
  action: actionWithPayload,
) {
  switch (action.type) {
    case REINITIALIZE:
      return INITIAL_STATE
    case SET_STEP:
      return state.set("step", action.payload)
    case SET_ACTIVE_FLIGHT:
      return state.set("flightId", action.payload)
    case LOAD_GUARANTEE_COVER_REQUEST:
      return state.set("loading", true)
    case LOAD_GUARANTEE_COVER_SUCCESS:
      return state
        .set(
          "price",
          new Price({
            ...action.res.guarantee_limits[0].price,
          }),
        )
        .set("loading", false)
    case LOAD_GUARANTEE_COVER_FAILURE:
    case CONFIRM_FLIGHT_SELECTION_FAILURE:
      return state.set("loading", false).set("step", STEPS.ERROR)
    case SUBMIT_SELECTED_FLIGHT_SUCCESS:
      return state.set("step", STEPS.THANK_YOU)
    case CONFIRM_FLIGHT_SELECTION_SUCCESS:
      return state.set("paxAtaId", action.res.guarantee_limit.id)
    default:
      return state
  }
}

// SELECTORS
export const loadingSelector = (state: any) => state.passengerAta.get("loading")
export const stepSelector = (state: any) => state.passengerAta.get("step")
export const selectedFlightIdSelector = (state: any) => state.passengerAta.get("flightId")
export const guaranteePriceSelector = (state: any) => state.passengerAta.get("price")
export const paxAtaIdSelector = (state: any) => state.passengerAta.get("paxAtaId")

export const shouldDisplayPassengerAtaSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  state => state.splitster,
  (data, splitster) => data.paxAta && splitster.get("PAX_ATA").value === "show",
)

export const isFlightEligibleForShotGuaranteeSelector: $FlowFixMe = createSelector(
  [bookingFlightsSelector, bookingFlightIdsSelector, (state, props) => props.flightId],
  (flights, flightIds, flightId) => {
    const currentFlightIndex = flightIds.indexOf(flightId)

    const currentFlight = flights.get(currentFlightIndex)
    const previousFlight = flights.get(currentFlightIndex - 1)

    return currentFlight.get("pnr") !== previousFlight.get("pnr")
  },
)

// ACTIONS
export const setStepAction = (step: Step) => ({
  type: SET_STEP,
  payload: step,
  _track: { step },
})

export const setSelectedFlight = (flightId: FlightId) => ({
  type: SET_ACTIVE_FLIGHT,
  payload: flightId,
})

export const reinitializeAction = () => ({
  type: REINITIALIZE,
})

// eslint-disable-next-line no-unused-vars
export const reinitialize = () => (dispatch: Dispatch<*>, getState: GetState) => {
  dispatch(reinitializeAction())
}

// eslint-disable-next-line flowtype/no-weak-types
export const loadGuaranteePrice = (flightId: FlightId) => async (
  dispatch: Dispatch<*>,
  getState: GetState,
) => {
  const state = getState()
  const bid = bidSelector(state)

  const ok = await dispatch({
    type: CONFIRM_FLIGHT_SELECTION,
    promise: confirmFlightSelection(bid, { segment_code: flightId }),
    _track: { flightId },
  })

  if (ok) {
    const promise = getGuaranteeCoverage(bid, flightId)

    await dispatch({
      type: LOAD_GUARANTEE_COVER,
      promise,
      _track: { flightId },
    })

    const guarantee = await promise
    const guaranteePrice = guarantee.guarantee_limits[0].price
    const guaranteeAction = guarantee.guarantee_limits[0].action

    if (guaranteeAction === PAX_ATA_ACTIONS.PAX && guaranteePrice.amount > 0) {
      dispatch(setStepAction(STEPS.PRICE))
      return
    }

    if (guaranteeAction === PAX_ATA_ACTIONS.AERO) {
      dispatch(setStepAction(STEPS.CONTACT_AIRLINE))
      return
    }

    if (guaranteePrice.amount === 0 || guaranteeAction === PAX_ATA_ACTIONS.CS) {
      dispatch(setStepAction(STEPS.PRICE_ERROR))
    }
  }
}

// eslint-disable-next-line flowtype/no-weak-types
export const confirm = (flightId: FlightId) => (dispatch: Dispatch<*>, getState: GetState) => {
  const state = getState()
  const bid = bidSelector(state)
  const price = guaranteePriceSelector(state)
  const paxAtaId = paxAtaIdSelector(state)

  dispatch({
    type: SUBMIT_SELECTED_FLIGHT,
    promise: confirmAta(bid, paxAtaId, {
      is_acknowledged: true,
      price: { amount: price.amount, currency: "EUR" },
    }),
    _track: { flightId, paxAtaId, price },
  })
}
